import { Checkbox } from "components/ui/checkbox";
import { Skeleton } from "components/ui/skeleton";
import { IDepositOption } from "src/@types/store-deposit-option";

import {
  DepositOptionDraft,
  OPERATION_CODE_PREFIX,
  SKELETON_ROW_COUNT,
  STORE_DEPOSIT_OPTION_LABELS,
} from "../types";

interface DepositOptionFormProps {
  readonly options: readonly IDepositOption[];
  readonly draft: DepositOptionDraft;
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  readonly onToggle: (operationCode: number, active: boolean) => void;
}

const listClass = "mt-6 max-w-xl rounded border border-slate-200";
const rowClass =
  "flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer";

const skeletonRows: readonly number[] = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_unused: unknown, index: number) => index,
);

function DepositOptionSkeleton() {
  return (
    <div className={listClass} data-testid="deposit-option-skeleton">
      {skeletonRows.map((row: number) => (
        <div key={row} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
          <Skeleton className="h-5 w-full" />
        </div>
      ))}
    </div>
  );
}

export function DepositOptionForm(props: DepositOptionFormProps) {
  if (props.isLoading) {
    return <DepositOptionSkeleton />;
  }

  if (props.options.length === 0) {
    return (
      <p className="mt-6 text-sm text-slate-500">
        {STORE_DEPOSIT_OPTION_LABELS.emptyOptions}
      </p>
    );
  }

  return (
    <div className={listClass}>
      {props.options.map((option: IDepositOption) => (
        <label key={option.operationCode} className={rowClass}>
          <Checkbox
            checked={props.draft[option.operationCode] === true}
            onCheckedChange={(checked: boolean | string) =>
              props.onToggle(option.operationCode, checked === true)
            }
            disabled={props.isSaving}
            aria-label={option.operationName}
          />
          <span className="text-sm text-slate-800">{option.operationName}</span>
          <span className="ml-auto text-xs text-slate-400">
            {OPERATION_CODE_PREFIX}
            {option.operationCode}
          </span>
        </label>
      ))}
    </div>
  );
}
